// @ts-nocheck
"use client";

import React, { useState, useEffect, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  MessageSquare, 
  Star, 
  CheckCircle, 
  Send,
  AlertCircle,
  User
} from "lucide-react";
import { toast } from "react-hot-toast";

interface FeedbackQuestion {
  id: string;
  text: string;
  questionType: 'text' | 'rating' | 'multiple_choice';
  isRequired: boolean;
  sortOrder: number;
  options?: any;
}

interface FeedbackCycle {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  cycleType: string;
  isAnonymous: boolean;
  questions: FeedbackQuestion[];
  targetUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string;
  };
}

interface Participant {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string;
  };
  invitedAt: string;
  isCompleted: boolean;
}

interface PublicFeedbackFormProps {
  cycle: FeedbackCycle;
  participant: Participant;
  token: string;
}

interface FormResponse {
  questionId: string;
  responseText?: string;
  responseRating?: number;
  responseChoice?: string;
}

export function PublicFeedbackForm({ cycle, participant, token }: PublicFeedbackFormProps) {
  const [responses, setResponses] = useState<Record<string, FormResponse>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Sort questions by sortOrder
  const sortedQuestions = useMemo(() => {
    if (!cycle.questions) return [];
    // Create a copy before sorting to avoid mutation
    return [...cycle.questions].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [cycle.questions]);

  useEffect(() => {
    // Initialize responses object
    const initialResponses: Record<string, FormResponse> = {};
    sortedQuestions.forEach(question => {
      initialResponses[question.id] = {
        questionId: question.id,
      };
    });
    setResponses(initialResponses);
  }, [sortedQuestions]);

  const handleResponseChange = (questionId: string, field: keyof FormResponse, value: any) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value,
      }
    }));
  };

  const validateResponse = (question: FeedbackQuestion, response: FormResponse): boolean => {
    if (!question.isRequired) return true;
    if (!response) return false;
    
    switch (question.questionType) {
      case 'text':
        return !!response.responseText?.trim();
      case 'rating':
        return typeof response.responseRating === 'number';
      case 'multiple_choice':
        return !!response.responseChoice;
      default:
        return true;
    }
  };

  const isFormValid = (): boolean => {
    return sortedQuestions.every(question => 
      validateResponse(question, responses[question.id])
    );
  };

  const getCompletedCount = (): number => {
    return sortedQuestions.filter(question => 
      validateResponse(question, responses[question.id])
    ).length;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast.error('Please complete all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const responseData = {
        responses: Object.values(responses).map(response => ({
          questionId: response.questionId,
          responseText: response.responseText || null,
          responseRating: response.responseRating || null,
          responseChoice: response.responseChoice || null,
        }))
      };

      const response = await fetch(`/api/feedback/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(responseData),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to submit feedback');
      }
      
      setShowSuccess(true);
      toast.success('Feedback submitted successfully');
      
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      toast.error(error.message || 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardContent className="pt-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Thank You!</h2>
            <p className="text-muted-foreground mb-6">
              Your feedback has been submitted successfully. We appreciate your input!
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-green-800">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Feedback Complete</span>
              </div>
              <p className="text-sm text-green-700 mt-1">
                {cycle.isAnonymous 
                  ? 'Your responses have been submitted anonymously.' 
                  : 'Your responses have been recorded and attributed to you.'
                }
              </p>
            </div>

            <div className="text-sm text-muted-foreground">
              <p>You may now close this page.</p>
              <p className="mt-1">
                Submitted for: <strong>{cycle.name}</strong>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const progress = ((getCompletedCount() / sortedQuestions.length) * 100);

  return (
    <div className="space-y-8">
      {/* Cycle Info */}
      <Card className="border-2 border-indigo-200 bg-indigo-50">
        <CardHeader>
          <CardTitle className="text-xl text-indigo-900">{cycle.name}</CardTitle>
          {cycle.description && (
            <CardDescription className="text-indigo-700">{cycle.description}</CardDescription>
          )}
        </CardHeader>
      </Card>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-muted-foreground mb-2">
          <span>Progress</span>
          <span>{getCompletedCount()} of {sortedQuestions.length} completed</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Questions */}
      <div className="space-y-6">
        {sortedQuestions.length === 0 && (
          <Card className="border-2 border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">No Questions Available</h3>
                <p className="text-yellow-700">
                  This feedback cycle doesn't have any questions configured. Please contact the administrator.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {sortedQuestions.map((question, index) => (
          <Card key={question.id} className="border-2 hover:border-border transition-colors">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <span className="bg-indigo-100 text-indigo-800 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    {question.text}
                  </CardTitle>
                  {question.isRequired && (
                    <Badge variant="secondary" size="sm" className="mt-2">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Required
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {question.questionType === 'text' && (
                <Textarea
                  placeholder="Share your thoughts..."
                  value={responses[question.id]?.responseText || ''}
                  onChange={(e) => handleResponseChange(question.id, 'responseText', e.target.value)}
                  className="min-h-[120px] resize-none"
                />
              )}
              
              {question.questionType === 'rating' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>1 - Poor</span>
                    <span>10 - Excellent</span>
                  </div>
                  <div className="flex gap-2 justify-center">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => handleResponseChange(question.id, 'responseRating', rating)}
                        className={`w-10 h-10 rounded-full border-2 font-semibold transition-all ${
                          responses[question.id]?.responseRating === rating
                            ? 'bg-indigo-600 text-white border-indigo-600 '
                            : 'bg-background text-foreground/90 border-border hover:border-indigo-300 '
                        }`}
                      >
                        {rating}
                      </button>
                    ))}
                  </div>
                  {responses[question.id]?.responseRating && (
                    <div className="text-center">
                      <Badge variant="outline" className="mt-2">
                        Selected: {responses[question.id]?.responseRating}
                      </Badge>
                    </div>
                  )}
                </div>
              )}
              
              {question.questionType === 'multiple_choice' && question.options && (
                <RadioGroup
                  value={responses[question.id]?.responseChoice || ''}
                  onValueChange={(value) => handleResponseChange(question.id, 'responseChoice', value)}
                  className="space-y-3"
                >
                  {(Array.isArray(question.options) ? question.options : JSON.parse(question.options || '[]')).map((option: string, optionIndex: number) => (
                    <div key={optionIndex} className="flex items-center space-x-3">
                      <RadioGroupItem value={option} id={`${question.id}-${optionIndex}`} />
                      <Label htmlFor={`${question.id}-${optionIndex}`} className="text-sm font-medium cursor-pointer">
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Submit Section */}
      <Card className="border-2 border-indigo-200 bg-indigo-50">
        <CardContent className="pt-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-foreground mb-2">Ready to Submit?</h3>
            <p className="text-muted-foreground mb-4">
              Please review your responses before submitting. You won't be able to edit them afterwards.
            </p>
            
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="text-sm text-muted-foreground">
                Completed: {getCompletedCount()} / {sortedQuestions.length}
              </div>
              {!isFormValid() && (
                <Badge variant="destructive" size="sm">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Missing required fields
                </Badge>
              )}
            </div>
            
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
              size="lg"
              className="min-w-[160px]"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Feedback
                </>
              )}
            </Button>
            
            {cycle.isAnonymous && (
              <p className="text-xs text-muted-foreground mt-3">
                Your feedback will be submitted anonymously
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
